/**
 * Presence and unread, derived from real signals only (spec #44 §4).
 *
 * Every function here is PURE and React-free on purpose. Presence is the
 * screen's most-read signal and unread is the one piece of state that
 * outlives a session; both must be testable as a table, without a render,
 * and neither may acquire a dependency on the provider that calls them.
 *
 * WHAT IS NOT HERE IS THE POINT. The prototype's `measuring` and
 * `training` states are dropped: no sibling contract carries them, and a
 * drawn-but-unrecorded state is exactly what HANDOFF §11 forbids.
 */

import type { AgentSettings } from '../types/agentSettings';
import type { TimelineEntry, TimelineHead } from '../types/timeline';
import type { PresenceState, WorkspaceQuestion } from '../types/workspace';

/** The subset of an agent presence actually reads. */
export type PresenceAgent = Pick<
  AgentSettings,
  'domain' | 'enabled' | 'configured'
>;

/** The subset of the head presence actually reads. */
export type PresenceHead = Pick<TimelineHead, 'active_agents'>;

/**
 * The five-state table (§4).
 *
 * PRECEDENCE, which the table states as a list and this function has to
 * resolve: configuration beats activity. `not-set-up` and `off` are facts
 * about what the tenant has decided and are what the sidebar greys out; a
 * stale `active_agents` entry for an agent the tenant switched off must
 * not render as live work. Then `working` (a cycle in flight) beats
 * `needs-you` (something waiting on the owner), and `quiet` is the
 * remainder.
 */
export function derivePresence(
  agent: PresenceAgent,
  head: PresenceHead | null,
  questions: readonly WorkspaceQuestion[]
): PresenceState {
  if (!agent.configured) return 'not-set-up';
  if (!agent.enabled) return 'off';
  if (head?.active_agents?.includes(agent.domain)) return 'working';
  if (questions.some((q) => q.owner_domain === agent.domain)) return 'needs-you';
  return 'quiet';
}

/**
 * The unread DOT: is this channel's newest activity newer than what the
 * reader has seen? (`head.latest_at[domain]` vs the stored watermark —
 * accepted delta 1.)
 *
 * No activity at all is read, not unread: an agent that has never done
 * anything is not something you missed.
 */
export function deriveUnread(
  latestAt: string | null | undefined,
  seenAt: string | null | undefined
): boolean {
  if (!latestAt) return false;
  if (!seenAt) return true;
  const latest = Date.parse(latestAt);
  const seen = Date.parse(seenAt);
  if (Number.isNaN(latest) || Number.isNaN(seen)) {
    // Unparseable on either side: fall back to an exact-string comparison
    // rather than silently reporting "read" on a timestamp we cannot read.
    return latestAt !== seenAt;
  }
  return latest > seen;
}

/**
 * The exact count for the channel that is OPEN — computed from the page
 * already fetched, which is what makes it honest and free. Every other
 * channel gets a dot (§Provisional 3).
 */
export function channelUnreadCount(
  entries: readonly TimelineEntry[],
  seenAt: string | null | undefined
): number {
  if (!seenAt) return entries.length;
  return entries.filter((entry) => deriveUnread(entry.occurred_at, seenAt))
    .length;
}

/** `mk.ws.seen.{tenant_id}.{domain}` — namespaced so an operator switching
 * tenants can never read the previous tenant's watermarks (§8 gate 5). */
export function seenKey(tenantId: string, domain: string): string {
  return `mk.ws.seen.${tenantId}.${domain}`;
}

/** The namespace prefix for one tenant — used to sweep on a tenant switch. */
export function seenPrefix(tenantId: string): string {
  return `mk.ws.seen.${tenantId}.`;
}

/**
 * Read a watermark. A storage that throws (private mode, storage disabled,
 * a browser that blocks third-party contexts) reads as "nothing seen" — a
 * dot too many is a survivable wrong; a crashed sidebar is not.
 */
export function readSeen(tenantId: string, domain: string): string | null {
  try {
    return localStorage.getItem(seenKey(tenantId, domain));
  } catch {
    return null;
  }
}

/** Write a watermark, tolerating a storage that refuses (quota, private). */
export function writeSeen(
  tenantId: string,
  domain: string,
  at: string
): void {
  try {
    localStorage.setItem(seenKey(tenantId, domain), at);
  } catch {
    /* unread degrades to "always unread" — never breaks the screen */
  }
}

/** Read every watermark this tenant has, as a `{domain: iso}` map. */
export function readSeenMap(
  tenantId: string,
  domains: readonly string[]
): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  domains.forEach((domain) => {
    map[domain] = readSeen(tenantId, domain);
  });
  return map;
}
